import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '@/pages/Home.vue';
import CouponList from '@/pages/CouponList.vue';
import ExchangeCoupon from '@/pages/ExchangeCoupon.vue';
import PurchaseCoupon from '@/pages/PurchaseCoupon.vue';
import PointHistory from '@/pages/PointHistory.vue';
import PointAction from '@/pages/PointAction.vue';
import PointActionComplete from '@/pages/PointActionComplete.vue';
import CouponAction from '@/pages/CouponAction.vue';
import CouponActionComplete from '@/pages/CouponActionComplete.vue';
import Error404 from '@/pages/Error404.vue';
import DialogCouponUsing from '@/components/organisms/coupon/DialogCouponUsing.vue';
import DialogCouponUsingMultiple from '@/components/organisms/coupon/DialogCouponUsingMultiple.vue';
import DialogCouponExchangeConfirm from '@/components/organisms/coupon/DialogCouponExchangeConfirm.vue';
import DialogCouponPurchaseConfirm from '@/components/organisms/coupon/DialogCouponPurchaseConfirm.vue';
import DialogPurchaseCouponDetail from '@/components/organisms/coupon/DialogPurchaseCouponDetail.vue';
import DialogPointHistoryDetail from '@/components/organisms/DialogPointHistoryDetail.vue';
import CouponActionError from '@/pages/CouponActionError.vue';
import PointActionError from '@/pages/PointActionError.vue';
import { i18n } from '@/plugins/i18n';
import CouponActionInstantwin from '@/pages/CouponActionInstantwin.vue';
import StampCard from '@/pages/StampCard.vue';
import StampCardList from '@/pages/StampCardList.vue';
import ShareCampaignList from '@/pages/ShareCampaignList.vue';
import ShareCampaign from '@/pages/ShareCampaign.vue';
import RaffleList from '@/pages/RaffleList.vue';
import Raffle from '@/pages/Raffle.vue';
import RaffleHistory from '@/pages/RaffleHistory.vue';
import DialogCouponDetail from '@/components/organisms/coupon/DialogCouponDetail.vue';
import Error429 from '@/pages/Error429.vue';
import MainError from '@/pages/MainError.vue';
import StampCardHistory from '@/pages/StampCardHistory.vue';

Vue.use(VueRouter);

const POINT_ENABLED = //true; // DEBUG
  `${
    process.env.NODE_ENV === 'local' ? window.POINT_ENABLED || process.env.VUE_APP_POINT_ENABLED : window.POINT_ENABLED
  }` === 'true';

/**
 * NOTE:
 * Code splitting（コード分割）を有効にしたい場合は、
 * `comsbi-saas/app` 側で動的ファイル読み込みの対応が必要 2021.03.08
 *
 * @see https://router.vuejs.org/ja/guide/advanced/lazy-loading.html
 */
export const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    /**
     * LIFF仕様上、rootを経由するのでタイトルはページ側で設定
     * @see https://sonicmoov-saas.atlassian.net/browse/CS-2114
     */
    // meta: { title: '会員証' },
  },
  {
    path: '/home',
    redirect: '/',
  },
  // CS-5480 外部からQRリーダー起動
  {
    path: '/qr',
    redirect: {
      path: '/',
      query: {
        qr: '1',
      },
    },
  },
  {
    path: '/coupon',
    name: 'CouponList',
    component: CouponList,
    meta: { title: i18n.t('title.coupon') },
    children: [
      {
        path: 'use',
        name: 'MultipleCouponUsing',
        component: DialogCouponUsingMultiple,
        meta: { title: i18n.t('title.coupon') },
        beforeEnter: (_, from, next) => {
          // NOTE: 画面リロード対策（クーポンBOXから遷移してダイアログが開かれることを想定しているため）
          if (from.path === '/') next('/coupon');

          next();
        },
      },
      {
        path: ':couponId',
        name: 'CouponUsing',
        component: DialogCouponUsing,
        meta: { title: i18n.t('title.coupon') },
        beforeEnter: (_, from, next) => {
          // NOTE: 画面リロード対策（クーポンBOXから遷移してダイアログが開かれることを想定しているため）
          if (from.path === '/') next('/coupon');

          next();
        },
      },
    ],
  },
  {
    path: '/exchange_coupon',
    name: 'ExchangeCoupon',
    component: ExchangeCoupon,
    meta: { title: POINT_ENABLED ? i18n.t('title.exchangeCoupon') : i18n.t('title.getCoupon') },
  },
  {
    path: '/exchange_coupon/:id',
    name: 'ExchangeCouponDetail',
    component: DialogCouponDetail,
    meta: { title: POINT_ENABLED ? i18n.t('title.exchangeCoupon') : i18n.t('title.getCoupon') },
    children: [
      {
        path: '/exchange_coupon/:id/confirm',
        name: 'CouponExchangeConfirm',
        component: DialogCouponExchangeConfirm,
        meta: { title: POINT_ENABLED ? i18n.t('title.exchangeCoupon') : i18n.t('title.getCoupon') },
      },
    ],
  },
  {
    path: '/purchase_coupon',
    name: 'PurchaseCoupon',
    component: PurchaseCoupon,
  },
  {
    path: '/purchase_coupon/:id',
    name: 'ExchangeCouponDetail',
    component: DialogPurchaseCouponDetail,
    children: [
      {
        path: '/purchase_coupon/:id/confirm',
        name: 'CouponPurchaseConfirm',
        component: DialogCouponPurchaseConfirm,
      },
    ],
  },
  {
    path: '/point_history',
    name: 'PointHistory',
    component: PointHistory,
    meta: { title: i18n.t('title.pointHistory') },
    children: [
      {
        path: ':transferId',
        name: 'PointHistoryDetail',
        component: DialogPointHistoryDetail,
        meta: { title: i18n.t('title.pointHistory') },
      },
    ],
  },
  {
    path: '/point_action',
    name: 'PointAction',
    component: PointAction,
    // meta: { title: 'ポイント獲得' }, // 獲得以外のアクションもあり得るのでtitle難しい...
    children: [
      {
        path: 'complete',
        name: 'PointActionComplete',
        component: PointActionComplete,
        // meta: { title: 'ポイント獲得' },
      },
      {
        path: 'error',
        name: 'PointActionError',
        component: PointActionError,
        // meta: { title: 'ポイント獲得' },
      },
    ],
  },
  {
    path: '/coupon_action',
    name: 'CouponAction',
    component: CouponAction,
    // meta: { title: 'クーポン獲得' }, // 獲得以外のアクションもあり得るのでtitle難しい...
    children: [
      {
        path: 'complete',
        name: 'CouponActionComplete',
        component: CouponActionComplete,
        // meta: { title: 'クーポン獲得' },
      },
      {
        path: 'error',
        name: 'CouponActionError',
        component: CouponActionError,
        // meta: { title: 'クーポン獲得' },
      },

      {
        // CS-6461
        path: 'instantwin',
        name: 'CouponActionInstantwin',
        component: CouponActionInstantwin,
        // meta: { title: 'クーポン獲得' },
      },
    ],
  },
  {
    // CS-7230
    path: '/stamp_card',
    name: 'StampCardList',
    component: StampCardList,
    meta: { title: i18n.t('title.stampCard') },
    children: [
      {
        path: ':id',
        name: 'StampCard',
        component: StampCard,
        meta: { title: i18n.t('title.stampCard') },
      },
      {
        path: ':id/history',
        name: 'StampCardHistory',
        component: StampCardHistory,
        meta: { title: i18n.t('title.stampCardHistory') },
      },
    ],
  },
  {
    // CS-7231
    path: '/share_campaign',
    name: 'ShareCampaignList',
    component: ShareCampaignList,
    meta: { title: i18n.t('title.shareCampaign') },
    children: [
      {
        path: ':id',
        name: 'ShareCampaign',
        component: ShareCampaign,
        meta: { title: i18n.t('title.shareCampaign') },
      },
    ],
  },
  {
    // CS-7930
    path: '/raffle',
    name: 'RaffleList',
    component: RaffleList,
    children: [
      {
        path: ':id',
        name: 'Raffle',
        component: Raffle,
        children: [],
      },
      {
        path: ':id/history',
        name: 'RaffleHistory',
        component: RaffleHistory,
      },
    ],
  },
  {
    path: '/error',
    name: 'MainError',
    component: MainError,
  },
  {
    path: '/error429',
    name: 'Error429',
    component: Error429,
  },
  {
    path: '/not_found',
    alias: '*',
    name: 'Error404',
    component: Error404,
    meta: { title: 'お探しのページは見つかりませんでした' },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: window.APP_BASE_URL || '/',
  routes,
});

router.beforeEach((to, _from, next) => {
  // タイトルを動的に設定
  document.title = to.meta?.title || process.env.VUE_APP_TITLE;
  next();
});

export default router;
